/**
 * usePlayback — RAF-based historical playback engine for A5-S3.
 *
 * Two modes:
 *   live   — position tracks "mock now" (HIST_NOW_TICK * HIST_TICK_MS); no advancement.
 *   replay — position advances through MOCK_HISTORY at the selected speed.
 *
 * Advancement rate: one 5-minute history tick per 500 ms of real time at 1x.
 * Doubles at 2x, quadruples at 4x. RAF accumulates wall-clock time so the
 * rate is stable regardless of frame rate; never skips ticks on fast screens.
 *
 * getHistoricalDensity — converts a Date position to a DensitySnapshot by
 * finding the nearest 5-minute tick in MOCK_HISTORY. Returns null if the
 * zone has no history entry.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { DensitySnapshot } from '@vigilo/shared';
import {
  HIST_DAY_MS, HIST_TICK_MS, HIST_NOW_TICK,
  type DensityDataPoint,
} from '../data/mock';

// ─── Types ─────────────────────────────────────────────────────────────────────

export type PlaybackMode  = 'live' | 'replay';
export type PlaybackSpeed = 1 | 2 | 4;
export type PlaybackRange = '1h' | '6h' | '24h';

export interface PlaybackControls {
  mode:       PlaybackMode;
  playing:    boolean;
  speed:      PlaybackSpeed;
  range:      PlaybackRange;
  position:   Date;
  rangeStart: Date;
  rangeEnd:   Date;
  setMode:    (m: PlaybackMode)  => void;
  togglePlay: () => void;
  setSpeed:   (s: PlaybackSpeed) => void;
  setRange:   (r: PlaybackRange) => void;
  seekTo:     (posMs: number)    => void;
}

// ─── Constants ─────────────────────────────────────────────────────────────────

const MOCK_NOW_MS = HIST_DAY_MS + HIST_NOW_TICK * HIST_TICK_MS;

function computeRangeStart(range: PlaybackRange): Date {
  if (range === '1h')  return new Date(MOCK_NOW_MS - 60 * 60_000);
  if (range === '6h')  return new Date(MOCK_NOW_MS - 6 * 60 * 60_000);
  return new Date(HIST_DAY_MS); // 24h from midnight
}

// ms of history time advanced per 500 ms real time at the given speed
function advanceMs(speed: PlaybackSpeed): number {
  return speed * HIST_TICK_MS; // 1 tick = 5 min per 500 ms at 1x
}

// ─── Hook ──────────────────────────────────────────────────────────────────────

export function usePlayback(): PlaybackControls {
  const [mode,    setModeState]  = useState<PlaybackMode>('live');
  const [playing, setPlaying]    = useState(false);
  const [speed,   setSpeedState] = useState<PlaybackSpeed>(1);
  const [range,   setRangeState] = useState<PlaybackRange>('1h');
  const [position, setPosition]  = useState<Date>(() => computeRangeStart('1h'));

  const rangeStart = computeRangeStart(range);
  const rangeEnd   = new Date(MOCK_NOW_MS);

  // RAF state refs — stable across renders
  const rafRef      = useRef<number | null>(null);
  const lastTsRef   = useRef<number | null>(null);
  const accumRef    = useRef(0);
  const speedRef    = useRef(speed);
  speedRef.current  = speed;

  // RAF-based playback advance
  useEffect(() => {
    if (mode !== 'replay' || !playing) {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      lastTsRef.current = null;
      accumRef.current  = 0;
      return;
    }

    function tick(ts: number) {
      if (lastTsRef.current === null) lastTsRef.current = ts;
      const delta = ts - lastTsRef.current;
      lastTsRef.current = ts;
      accumRef.current += delta;

      // Advance one step per 500 ms of real time
      if (accumRef.current >= 500) {
        accumRef.current -= 500;
        setPosition(prev => {
          const next = new Date(prev.getTime() + advanceMs(speedRef.current));
          if (next.getTime() >= MOCK_NOW_MS) {
            setPlaying(false);
            return new Date(MOCK_NOW_MS);
          }
          return next;
        });
      }

      rafRef.current = requestAnimationFrame(tick);
    }

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = null;
    };
  }, [mode, playing]);

  const setMode = useCallback((m: PlaybackMode) => {
    setModeState(m);
    if (m === 'replay') {
      setPosition(computeRangeStart(range));
      setPlaying(false);
    } else {
      setPlaying(false);
    }
  }, [range]);

  const setRange = useCallback((r: PlaybackRange) => {
    setRangeState(r);
    setPosition(computeRangeStart(r));
    setPlaying(false);
  }, []);

  const togglePlay = useCallback(() => setPlaying(p => !p), []);

  const setSpeed = useCallback((s: PlaybackSpeed) => {
    setSpeedState(s);
    speedRef.current = s;
  }, []);

  const seekTo = useCallback((posMs: number) => {
    setPosition(new Date(Math.max(HIST_DAY_MS, Math.min(MOCK_NOW_MS, posMs))));
    setPlaying(false);
  }, []);

  return {
    mode, playing, speed, range, position,
    rangeStart, rangeEnd,
    setMode, togglePlay, setSpeed, setRange, seekTo,
  };
}

// ─── getHistoricalDensity ─────────────────────────────────────────────────────
// Returns a DensitySnapshot by finding the nearest 5-minute tick in history.
// Heatmap re-renders only when the tick index changes (5-minute boundaries),
// not on every animation frame — callers should memoize by tick index.

export function getHistoricalDensity(
  zoneId:   string,
  position: Date,
  history:  Record<string, DensityDataPoint[]>,
): DensitySnapshot | null {
  const pts = history[zoneId];
  if (!pts || pts.length === 0) return null;

  const startMs  = new Date(pts[0].timestamp).getTime();
  const rawIdx   = Math.round((position.getTime() - startMs) / HIST_TICK_MS);
  const idx      = Math.max(0, Math.min(pts.length - 1, rawIdx));
  const pt       = pts[idx];
  const prevPt   = idx > 0 ? pts[idx - 1] : null;

  const trendDirection =
    prevPt === null                                         ? 'stable'
    : pt.capacityUtilisation > prevPt.capacityUtilisation + 0.5 ? 'rising'
    : pt.capacityUtilisation < prevPt.capacityUtilisation - 0.5 ? 'falling'
    : 'stable';

  return {
    zoneId,
    occupancyCount:      pt.occupancyCount,
    densityScore:        pt.capacityUtilisation,
    capacityUtilisation: pt.capacityUtilisation,
    trendDirection,
    trendRate: prevPt
      ? Math.abs(pt.capacityUtilisation - prevPt.capacityUtilisation)
      : 0,
    timestamp: pt.timestamp,
  };
}
